<template>
  <div class="login-container">
    <h2 class="title">登录</h2>

    <!-- 手机号输入框 -->
    <div class="input-group">
      <van-field v-model="phoneNumber" placeholder="输入手机号" :border="false">
        <template #label>
          <div class="country-code" @click="showAreaCodePicker = true">
            <SvgIcon name="phone" />
            <span>+{{ phoneAreaPrefix }}</span>
            <van-icon name="arrow-down" size="14" color="#666666" />
          </div>
        </template>
      </van-field>
    </div>

    <!-- 添加区号选择器弹出层 -->
    <van-popup v-model:show="showAreaCodePicker" position="bottom">
      <van-picker :columns="areaCodeColumns" @confirm="onAreaCodeConfirm" @cancel="showAreaCodePicker = false"
        :title="'选择区号'" show-toolbar />
    </van-popup>

    <!-- 密码输入框 -->
    <div class="input-group">
      <van-field v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="输入密码" :border="false">
        <template #label>
          <SvgIcon name="pwd" />
        </template>
        <template #right-icon>
          <van-icon :name="showPassword ? 'eye-o' : 'closed-eye'" @click="togglePasswordVisibility" />
        </template>
      </van-field>
    </div>

    <!-- 忘记密码和注册 -->
    <div class="action-links">
      <span @click="forgetPassword">忘记密码</span>
      <span @click="register">注册</span>
    </div>

    <!-- 登录按钮 -->
    <van-button type="primary" block round @click="submitForm">登录</van-button>

    <!-- 协议勾选 -->
    <div class="agreement">
      <van-checkbox v-model="agreed" shape="round" icon-size="12">
        登录即表示同意<a href="https://www.jingchat.cn/userProtocol.html" target="_blank">《服务条款》</a>和<a
          href="https://www.hnmengyuqianfang.cn/features.html" target="_blank ">《隐私协议》</a>
      </van-checkbox>
    </div>

    <!-- 验证码登录 -->
    <div class="verification-login">
      <div class="verification-login-item" @click="goToVerificationLogin">
        <van-icon name="chat-o" color="#FF6B00" size="28" />
        <span>验证码登录</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { showToast } from 'vant'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

const router = useRouter()
const store = useStore()

// 定义表单数据
const phoneAreaPrefix = ref('86')
const phoneNumber = ref('')
const password = ref('')
const agreed = ref(false)
const showPassword = ref(false)
const showAreaCodePicker = ref(false)

// 区号数据
const areaCodeColumns = [
  { text: '中国 +86', value: '86' },
  { text: '中国香港 +852', value: '852' },
  { text: '中国澳门 +853', value: '853' },
  { text: '中国台湾 +886', value: '886' },
  { text: '日本 +81', value: '81' },
  { text: '韩国 +82', value: '82' },
  { text: '美国 +1', value: '1' },
]

// 表单校验规则
const validatePhoneNumber = () => {
  if (!phoneNumber.value) {
    showToast('请输入手机号')
    return false
  }
  if (!/^1[3-9]\d{9}$/.test(phoneNumber.value)) {
    showToast('请输入正确的手机号')
    return false
  }
  return true
}

const validatePassword = () => {
  if (!password.value) {
    showToast('请输入密码')
    return false
  }
  if (password.value.length < 6) {
    showToast('密码不能少于6位')
    return false
  }
  return true
}

const validateAgreement = () => {
  if (!agreed.value) {
    showToast('请先同意隐私政策')
    return false
  }
  return true
}

// 提交表单
const submitForm = () => {
  if (!validatePhoneNumber()) return
  if (!validatePassword()) return
  if (!validateAgreement()) return

  const data = {
    captcha: '',
    phoneAreaPrefix: phoneAreaPrefix.value,
    phoneNumber: phoneNumber.value,
    password: password.value,
    type: 0
  }
  store.dispatch('user/login', data)
}

// 忘记密码
const forgetPassword = () => {
  router.push('/ResetPwd')
}

// 注册
const register = () => {
  router.push('/Register')
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const goToVerificationLogin = () => {
  router.push('/LoginCode')
}

// 区号选择确认处理函数
const onAreaCodeConfirm = (item: any) => {
  phoneAreaPrefix.value = item.selectedValues[0]
  showAreaCodePicker.value = false
}

</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  background-color: #fff;
  padding: 20px 32px;
  box-sizing: border-box;

  .title {
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    margin: 40px 0;
  }

  .input-group {
    background: transparent;
    border-bottom: 1px solid #ebedf0;
    border-radius: 0;
    margin-bottom: 16px;
    overflow: hidden;

    :deep(.van-field) {
      background: transparent;

      &::after {
        display: none;
      }
    }

    :deep(.van-field__label) {
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  .country-code {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    border-right: 1px solid #ebedf0;
    margin-right: 8px;

    span {
      font-size: 14px;
      color: #333;
      font-weight: normal;
    }
  }

  .action-links {
    display: flex;
    justify-content: space-between;
    margin: 16px 0 24px;

    span {
      color: #666;
      font-size: 14px;
    }
  }

  .agreement {
    text-align: center;
    font-size: 12px;
    color: #999;
    margin: 16px 0;

    :deep(.van-checkbox__label) {
      color: #999;
    }

    a {
      color: $primary-color;
      text-decoration: none;
    }
  }

  .verification-login {
    width: 100%;
    text-align: center;
    margin-top: 60px;

    .verification-login-item {
      width: fit-content;
      display: inline-flex;
      flex-direction: column;
      align-items: center;

      .van-icon {
        display: block;
        margin: 0 auto 8px;
      }

      span {
        font-size: 14px;
        color: #666;
      }
    }
  }
}
</style>
